import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import emailService from '../../services/emailService';

interface ImportGoogleSheetsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: () => void;
}

interface GoogleSheetsImportData {
  sheetUrl: string;
  sheetName: string;
  range: string;
  fieldMapping: Record<string, string>;
  duplicateHandling: 'skip' | 'update' | 'create_new';
  previewData: Record<string, any>[];
  headers: string[];
}

const ImportGoogleSheetsModal: React.FC<ImportGoogleSheetsModalProps> = ({ 
  isOpen, 
  onClose, 
  onImportComplete 
}) => {
  const [importData, setImportData] = useState<GoogleSheetsImportData>({
    sheetUrl: '',
    sheetName: '',
    range: '',
    fieldMapping: {},
    duplicateHandling: 'skip',
    previewData: [],
    headers: []
  });
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  
  const standardFields = {
    email: 'Email Address',
    first_name: 'First Name',
    last_name: 'Last Name',
    company: 'Company',
    position: 'Position',
    phone: 'Phone'
  };
  
  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setImportData(prev => ({
      ...prev,
      sheetUrl: e.target.value
    }));
  };
  
  const handleSheetNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setImportData(prev => ({
      ...prev,
      sheetName: e.target.value
    }));
  };
  
  const handleRangeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setImportData(prev => ({
      ...prev,
      range: e.target.value
    }));
  };
  
  const handleFieldMapping = (csvField: string, standardField: string) => {
    setImportData(prev => ({
      ...prev,
      fieldMapping: {
        ...prev.fieldMapping,
        [csvField]: standardField
      }
    }));
  };
  
  const fetchSheetData = async () => {
    // Validate URL
    if (!importData.sheetUrl) {
      toast.error('Please enter a Google Sheets URL');
      return;
    }
    
    // In a real implementation, we would make an API call to our backend
    // which would use the Google Sheets API to fetch the data
    
    // For now, we'll simulate with mock data
    // In a production implementation, this would be replaced with an actual API call
    // to fetch the data from Google Sheets
    
    // Simulate API call delay
    setIsImporting(true);
    
    try {
      // Simulate fetching data
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Mock headers and data
      const mockHeaders = ['First Name', 'Last Name', 'Email', 'Company', 'Position'];
      const mockData = [
        { 'First Name': 'John', 'Last Name': 'Doe', 'Email': 'john@example.com', 'Company': 'Acme Inc', 'Position': 'Manager' },
        { 'First Name': 'Jane', 'Last Name': 'Smith', 'Email': 'jane@example.com', 'Company': 'Globex Corp', 'Position': 'Developer' },
        { 'First Name': 'Bob', 'Last Name': 'Johnson', 'Email': 'bob@example.com', 'Company': 'Initech', 'Position': 'Designer' }
      ];
      
      setImportData(prev => ({
        ...prev,
        headers: mockHeaders,
        previewData: mockData
      }));
      
      toast.success('Sheet data loaded successfully');
    } catch (error) {
      console.error('Error fetching sheet data:', error);
      toast.error('Failed to load sheet data');
    } finally {
      setIsImporting(false);
    }
  };
  
  const handleImport = async () => {
    if (importData.headers.length === 0) {
      toast.error('Please load sheet data first');
      return;
    }
    
    if (Object.keys(importData.fieldMapping).length === 0) {
      toast.error('Please map at least one field');
      return;
    }
    
    setIsImporting(true);
    setImportProgress(0);
    
    try {
      // In a production implementation, this would make an actual API call
      // For now, we'll simulate the import process
      
      // Simulate progress
      for (let i = 0; i <= 100; i += 10) {
        setImportProgress(i);
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // In a real implementation, we would call the email service:
      // const result = await emailService.importGoogleSheetsContacts(
      //   importData.sheetUrl,
      //   importData.sheetName,
      //   importData.range,
      //   importData.fieldMapping,
      //   importData.duplicateHandling
      // );
      
      toast.success('Contacts imported successfully');
      onImportComplete();
      onClose();
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Failed to import contacts');
    } finally {
      setIsImporting(false);
      setImportProgress(0);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-screen overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Import Contacts from Google Sheets</h3>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              &times;
            </button>
          </div>
        </div>
        
        <div className="px-6 py-4">
          {!importData.headers.length ? (
            <div className="space-y-6">
              <div>
                <h4 className="text-lg font-semibold mb-4">Google Sheets Information</h4>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="sheetUrl" className="block text-sm font-medium text-gray-700 mb-1">
                      Google Sheets URL*
                    </label>
                    <input
                      type="url"
                      id="sheetUrl"
                      value={importData.sheetUrl}
                      onChange={handleUrlChange}
                      placeholder="https://docs.google.com/spreadsheets/d/..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      Make sure your Google Sheet is shared with "Anyone with the link" access
                    </p>
                  </div>
                  
                  <div>
                    <label htmlFor="sheetName" className="block text-sm font-medium text-gray-700 mb-1">
                      Sheet Name (optional)
                    </label>
                    <input
                      type="text"
                      id="sheetName"
                      value={importData.sheetName}
                      onChange={handleSheetNameChange}
                      placeholder="Sheet1"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="range" className="block text-sm font-medium text-gray-700 mb-1">
                      Range (optional)
                    </label>
                    <input
                      type="text"
                      id="range"
                      value={importData.range}
                      onChange={handleRangeChange}
                      placeholder="A1:E100"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      Specify range if you only want to import a portion of the sheet
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end">
                <button
                  onClick={fetchSheetData}
                  disabled={isImporting || !importData.sheetUrl}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md disabled:opacity-50"
                >
                  {isImporting ? 'Loading...' : 'Load Sheet Data'}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div>
                <h4 className="text-lg font-semibold mb-4">Map Sheet Fields</h4>
                <div className="grid grid-cols-2 gap-4">
                  {importData.headers.map((header, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <span className="text-sm font-medium w-32">{header}:</span>
                      <select
                        value={importData.fieldMapping[header] || ''}
                        onChange={(e) => handleFieldMapping(header, e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                      >
                        <option value="">Skip this field</option>
                        {Object.entries(standardFields).map(([key, label]) => (
                          <option key={key} value={key}>{label}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-lg font-semibold mb-4">Duplicate Handling</h4>
                <div className="space-y-2">
                  {[
                    { value: 'skip', label: 'Skip duplicates' },
                    { value: 'update', label: 'Update existing contacts' },
                    { value: 'create_new', label: 'Create new contacts anyway' }
                  ].map(option => (
                    <label key={option.value} className="flex items-center">
                      <input
                        type="radio"
                        name="duplicateHandling"
                        value={option.value}
                        checked={importData.duplicateHandling === option.value}
                        onChange={(e) => setImportData(prev => ({...prev, duplicateHandling: e.target.value as any}))}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      <span className="ml-2 text-sm">{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {importData.previewData.length > 0 && (
                <div>
                  <h4 className="text-lg font-semibold mb-4">Preview</h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full border border-gray-300">
                      <thead>
                        <tr className="bg-gray-50">
                          {importData.headers.map((header, index) => (
                            <th key={index} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase border-b">
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {importData.previewData.map((row, index) => (
                          <tr key={index} className="border-b">
                            {importData.headers.map((header, colIndex) => (
                              <td key={colIndex} className="px-4 py-2 text-sm text-gray-900">
                                {row[header]}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {isImporting && (
                <div>
                  <div className="flex justify-between text-sm text-gray-600 mb-1">
                    <span>Importing contacts...</span>
                    <span>{importProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${importProgress}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        
        <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          {importData.headers.length > 0 && (
            <button
              onClick={handleImport}
              disabled={isImporting || Object.keys(importData.fieldMapping).length === 0}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md disabled:opacity-50"
            >
              {isImporting ? 'Importing...' : 'Import Contacts'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportGoogleSheetsModal;
